// src/pages/vendors/purchase-order-form-components/NotesAndTermsSection.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  InputAdornment,
  alpha,
  useTheme,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Notes as NotesIcon,
  Gavel as GavelIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  Save as SaveIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { supabase } from '../../../supabaseClient';
import { useParams } from 'react-router-dom';
import { usePermissions } from '../../../utils/permissions';

const NotesAndTermsSection = ({ purchaseOrder, onPOChange, canEdit = true }) => {
  const theme = useTheme();
  const { companyId } = useParams();
  const { canDeleteVendorPurchaseOrders } = usePermissions();
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch templates
  useEffect(() => {
    if (companyId) {
      fetchTemplates();
    }
  }, [companyId]);

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('terms_templates')
        .select('*')
        .eq('company_id', companyId)
        .in('template_type', ['po', 'both'])
        .order('template_name');

      if (error) throw error;
      setTemplates(data || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const handleLoadTemplate = (template) => {
    onPOChange('terms_and_conditions', template.terms_content);
    setLoadDialogOpen(false);
    setTemplateSearchTerm('');
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      setSnackbar({ open: true, message: 'Please enter a template name', severity: 'error' });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('terms_templates')
        .insert([{
          company_id: companyId,
          template_name: templateName.trim(),
          template_type: 'both',
          terms_content: purchaseOrder.terms_and_conditions || '',
          created_by: user?.id
        }]);

      if (error) throw error;

      await fetchTemplates();
      setTemplateName('');
      setSaveDialogOpen(false);
      setSnackbar({ open: true, message: 'Template saved successfully!', severity: 'success' });
    } catch (err) {
      console.error('Error saving template:', err);
      setSnackbar({ open: true, message: 'Failed to save template: ' + err.message, severity: 'error' });
    }
  };

  const handleDeleteClick = (template, e) => {
    e.stopPropagation();
    if (!canDeleteVendorPurchaseOrders()) {
      setSnackbar({ open: true, message: 'You do not have permission to delete templates.', severity: 'error' });
      return;
    }
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    if (!canDeleteVendorPurchaseOrders()) {
      setSnackbar({ open: true, message: 'You do not have permission to delete templates.', severity: 'error' });
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      return;
    }

    try {
      const { error } = await supabase
        .from('terms_templates')
        .delete()
        .eq('id', templateToDelete.id);

      if (error) throw error;

      await fetchTemplates();
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      setSnackbar({ open: true, message: 'Template deleted successfully!', severity: 'success' });
    } catch (err) {
      console.error('Error deleting template:', err);
      setSnackbar({ open: true, message: 'Failed to delete template: ' + err.message, severity: 'error' });
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  const filteredTemplates = templates.filter(template =>
    template.template_name?.toLowerCase().includes(templateSearchTerm.toLowerCase())
  );

  return (
    <Paper sx={{ 
      p: 3, 
      mb: 3, 
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
    }}>
      <Typography variant="h6" sx={{ 
        mb: 3, 
        fontWeight: 500, 
        fontSize: '1.2rem',
        color: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <NotesIcon sx={{ fontSize: 28 }} />
        Notes & Terms
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Payment Terms"
            fullWidth
            value={purchaseOrder.payment_terms || ''}
            onChange={(e) => onPOChange('payment_terms', e.target.value)}
            placeholder="e.g., Net 30"
            disabled={!canEdit}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                minHeight: 56,
                '& input': {
                  py: 2.5,
                  px: 3,
                  fontSize: '0.9rem',
                  fontWeight: 500
                },
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                },
                '&.Mui-focused': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' }
              }
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Payment Method"
            fullWidth
            value={purchaseOrder.payment_method || ''}
            onChange={(e) => onPOChange('payment_method', e.target.value)}
            placeholder="e.g., Check, Wire Transfer"
            disabled={!canEdit}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                minHeight: 56,
                '& input': {
                  py: 2.5,
                  px: 3,
                  fontSize: '0.9rem',
                  fontWeight: 500
                },
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                },
                '&.Mui-focused': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' }
              }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Notes"
            fullWidth
            multiline
            rows={3}
            value={purchaseOrder.notes || ''}
            onChange={(e) => onPOChange('notes', e.target.value)}
            placeholder="Additional notes for the supplier"
            disabled={!canEdit}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                alignItems: 'flex-start !important',
                paddingTop: '8px !important',
                paddingBottom: '8px !important',
                '& textarea': {
                  paddingTop: '8px !important',
                  paddingBottom: '8px !important',
                  paddingLeft: '12px !important',
                  paddingRight: '12px !important',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  textAlign: 'left !important',
                  alignSelf: 'flex-start',
                  lineHeight: 1.5,
                  verticalAlign: 'top',
                  margin: 0
                },
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                },
                '&.Mui-focused': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' },
                alignItems: 'flex-start !important',
                '& .MuiInputBase-inputMultiline': {
                  paddingTop: '8px !important',
                  paddingBottom: '8px !important',
                  paddingLeft: '12px !important',
                  paddingRight: '12px !important',
                  textAlign: 'left !important'
                }
              }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Special Instructions"
            fullWidth
            multiline
            rows={3}
            value={purchaseOrder.special_instructions || ''}
            onChange={(e) => onPOChange('special_instructions', e.target.value)}
            placeholder="Special instructions for delivery or handling"
            disabled={!canEdit}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                alignItems: 'flex-start !important',
                paddingTop: '8px !important',
                paddingBottom: '8px !important',
                '& textarea': {
                  paddingTop: '8px !important',
                  paddingBottom: '8px !important',
                  paddingLeft: '12px !important',
                  paddingRight: '12px !important',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  textAlign: 'left !important',
                  alignSelf: 'flex-start',
                  lineHeight: 1.5,
                  verticalAlign: 'top',
                  margin: 0
                },
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                },
                '&.Mui-focused': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' },
                alignItems: 'flex-start !important',
                '& .MuiInputBase-inputMultiline': {
                  paddingTop: '8px !important',
                  paddingBottom: '8px !important',
                  paddingLeft: '12px !important',
                  paddingRight: '12px !important',
                  textAlign: 'left !important'
                }
              }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          {canEdit && (
            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
              <Button
                variant="outlined"
                startIcon={<DescriptionIcon />}
                onClick={() => setLoadDialogOpen(true)}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}
              >
                Load Template
              </Button>
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={() => setSaveDialogOpen(true)}
                disabled={!purchaseOrder.terms_and_conditions}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}
              >
                Save as Template
              </Button>
            </Box>
          )}
          <TextField
            label="Terms and Conditions"
            fullWidth
            multiline
            rows={25}
            value={purchaseOrder.terms_and_conditions || ''}
            onChange={(e) => onPOChange('terms_and_conditions', e.target.value)}
            placeholder="Terms and conditions for this purchase order"
            disabled={!canEdit}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                alignItems: 'flex-start !important',
                paddingTop: '8px !important',
                paddingBottom: '8px !important',
                '& textarea': {
                  paddingTop: '8px !important',
                  paddingBottom: '8px !important',
                  paddingLeft: '12px !important',
                  paddingRight: '12px !important',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  textAlign: 'left !important',
                  alignSelf: 'flex-start',
                  lineHeight: 1.5,
                  verticalAlign: 'top',
                  margin: 0
                },
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                },
                '&.Mui-focused': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }
            }}
            sx={{
              mt: 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' },
                alignItems: 'flex-start'
              }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Internal Notes"
            fullWidth
            multiline
            rows={2}
            value={purchaseOrder.internal_notes || ''}
            onChange={(e) => onPOChange('internal_notes', e.target.value)}
            placeholder="Internal notes (not visible to supplier)"
            disabled={!canEdit}
            InputProps={{
              sx: { 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                alignItems: 'flex-start !important',
                paddingTop: '8px !important',
                paddingBottom: '8px !important',
                '& textarea': {
                  paddingTop: '8px !important',
                  paddingBottom: '8px !important',
                  paddingLeft: '12px !important',
                  paddingRight: '12px !important',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  textAlign: 'left !important',
                  alignSelf: 'flex-start',
                  lineHeight: 1.5,
                  verticalAlign: 'top',
                  margin: 0
                },
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                },
                '&.Mui-focused': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' },
                alignItems: 'flex-start !important',
                '& .MuiInputBase-inputMultiline': {
                  paddingTop: '8px !important',
                  paddingBottom: '8px !important',
                  paddingLeft: '12px !important',
                  paddingRight: '12px !important',
                  textAlign: 'left !important'
                }
              }
            }}
          />
        </Grid>
      </Grid>

      {/* Load Template Dialog */}
      <Dialog
        open={loadDialogOpen}
        onClose={() => {
          setLoadDialogOpen(false);
          setTemplateSearchTerm('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
            Load Terms Template
          </Typography>
          <IconButton onClick={() => {
            setLoadDialogOpen(false);
            setTemplateSearchTerm('');
          }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search templates..."
            value={templateSearchTerm}
            onChange={(e) => setTemplateSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              sx: { borderRadius: 2, mb: 2 }
            }}
          />
          {filteredTemplates.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              {templates.length === 0 ? 'No saved templates. Create one by clicking "Save as Template".' : 'No templates match your search.'}
            </Typography>
          ) : (
            <List>
              {filteredTemplates.map((template) => (
                <ListItem key={template.id} disablePadding>
                  <ListItemButton
                    onClick={() => handleLoadTemplate(template)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                      }
                    }}
                  >
                    <ListItemText
                      primary={template.template_name}
                      secondary={template.is_default ? 'Default' : ''}
                    />
                    {canDeleteVendorPurchaseOrders() && (
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={(e) => handleDeleteClick(template, e)}
                          sx={{
                            color: theme.palette.error.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.error.main, 0.1)
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setLoadDialogOpen(false);
            setTemplateSearchTerm('');
          }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => {
          setSaveDialogOpen(false);
          setTemplateName('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
            Save as Template
          </Typography>
          <IconButton onClick={() => {
            setSaveDialogOpen(false);
            setTemplateName('');
          }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Template Name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Enter template name"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSaveDialogOpen(false);
            setTemplateName('');
          }}>
            Cancel
          </Button>
          <Button onClick={handleSaveTemplate} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setTemplateToDelete(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: 'error.main' }}>
            Delete Template
          </Typography>
          <IconButton onClick={() => {
            setDeleteDialogOpen(false);
            setTemplateToDelete(null);
          }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the template "{templateToDelete?.template_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setTemplateToDelete(null);
          }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default NotesAndTermsSection;